import * as React from 'react'
import * as cn from 'classnames'
import { counter } from '../../../util/csm/csm'
import { fireDraperTrackingEvent, logError } from './utils/metrics'
import * as styles from './DraperPlayer.module.scss'
import fetch from './utils/fetch'
import { DraperPlayerPlacement, DraperPlayerProps, DraperPlayerState } from './types'
import { canTakeLock, hasLock, takeLock, releaseLock } from './utils/locks'
import { DraperPlayerControls } from './DraperPlayer/Controls'

const LOAD_ERROR_TIMEOUT = 4000
const PLAYER_COUNTER_PREFIX = 'DVWeb::Storefront::PreRollPlayer'
const RESUME_TIMER = 1500


export default class DraperPlayer extends React.PureComponent<DraperPlayerProps, DraperPlayerState> {

  /* Class members */

  playHandler: () => void
  stopHandler: () => void
  updateHandler: () => void
  errorHandler: () => void
  waitingHandler: () => void

  videoStarted: boolean
  firstQuartileReached: boolean
  midpointReached: boolean
  thirdQuartileReached: boolean
  expectedReadyState: number

  wrappers: {
    videoWrapper?: HTMLAnchorElement
    videoPlayer?: HTMLVideoElement
  } = {}

  /* React lifecycle methods (except render) */

  constructor(props: DraperPlayerProps) {
    super(props)
    this.state = {
      hasError: false,
      playing: false,
      paused: false
    }
    this.playHandler = null
    this.stopHandler = null
    this.updateHandler = null

    this.videoStarted = false
    this.firstQuartileReached = false
    this.midpointReached = false
    this.thirdQuartileReached = false
    this.expectedReadyState = 4
  }

  componentDidMount(): void {
    this.setState({ originalVolume: this.wrappers.videoPlayer.volume })
    this.wrappers.videoPlayer.preload = 'auto'
  }

  componentDidUpdate(prevProps: DraperPlayerProps) {
    if (this.state.playing && this.props.videoContext !== prevProps.videoContext) {
      if (hasLock(this.props, this)) {
        setTimeout(() => {
          if (this.state.playing && hasLock(this.props, this)) {
            this.resumeWhenPaused()
          }
        }, RESUME_TIMER)
      } else {
        if (this.props.playerIsActive) {
          this.stop()
        } else {
          this.pauseButKeepVisible()
        }
      }
    }

    if (this.props.prerolls != prevProps.prerolls) {
      this.videoStarted = false
      this.firstQuartileReached = false
      this.midpointReached = false
      this.thirdQuartileReached = false
    }
  }

  componentWillUnmount() {
    this.removeEventListeners()
  }

  /* Player controls */

  startPlayer() {
    this.playHandler = () => this.playAssumingReady()
    this.stopHandler = () => this.onPlaybackComplete()
    this.updateHandler = () => this.onTimeUpdate()
    this.errorHandler = () => this.onLoadError()
    this.waitingHandler = () => this.onWaiting()
    this.addEventListeners()

    if (this.props.playerIsActive || canTakeLock(this.props)) {
      takeLock(this.props, this)
    } else {
      return
    }

    // Sometimes in Firefox when the video is in the cache of the browser
    // the canplaythrough event is fired before we have had a chance to
    // register the event
    if (this.wrappers.videoPlayer.readyState >= this.expectedReadyState) {
      if (this.props.placement === DraperPlayerPlacement.Superhero) {
        this.wrappers.videoPlayer && this.wrappers.videoPlayer.load()
      }
      this.playHandler()
    }
  }

  playAssumingReady() {
    if (this.wrappers.videoPlayer && this.wrappers.videoPlayer.readyState >= this.expectedReadyState) {
      const playPromise = this.wrappers.videoPlayer.play()
      if (typeof playPromise !== 'undefined') {
        playPromise.then(() => {
          !this.videoStarted && fireDraperTrackingEvent(this.props.prerolls, 'defaultImpression')
          this.onPlayStart()
        }).catch((err) => {
          this.onPlayError(err)
          logError(`Pre-Roll Player Failed to play: ${err}.`, err)
        })
      } else { // This is for IE11 and below as it does not support promises https://caniuse.com/#feat=promises
        try {
          !this.videoStarted && fireDraperTrackingEvent(this.props.prerolls, 'defaultImpression')
          this.wrappers.videoPlayer.play()
          this.onPlayStart()
        } catch (err) {
          this.onPlayError(err)
          logError(`Pre-Roll Player Failed to play: ${err}.`, err)
        }
      }
    }
  }

  pauseButKeepVisible() {
    this.wrappers.videoPlayer && this.wrappers.videoPlayer.pause()
    !this.state.paused && fireDraperTrackingEvent(this.props.prerolls, 'pause')
    this.setState({ paused: true })
  }

  resumeWhenPaused() {
    this.wrappers.videoPlayer && this.wrappers.videoPlayer.play()
    this.state.paused && fireDraperTrackingEvent(this.props.prerolls, 'resume')
    this.setState({ paused: false })
  }

  stop() {
    this.setState({
      playing: false
    }, () => {
      this.props.parentListeners && this.props.parentListeners.onPlayStop && this.props.parentListeners.onPlayStop()

      this.wrappers.videoPlayer.pause()

      this.removeEventListeners()

      releaseLock(this.props, this)
    })
  }

  toggleMute = (event: React.MouseEvent) => {
    if (event.button !== 0) return // primary/left click
    event.preventDefault()
    const newMuteVal = !this.props.videoContext.muted
    this.props.videoContext.updateContext({ ...this.props.videoContext, muted: !this.props.videoContext.muted })
    this.onMute(newMuteVal)
  }

  toggleFullscreen = (event: React.MouseEvent) => {
    if (event.button !== 0) return // primary/left click
    return this.updateFullscreen(!(this.props.videoContext.fullScreen))
  }

  updateFullscreen(newValue: boolean) {
    this.updatePageScrolling(newValue ? 'hidden' : 'visible')
    return this.props.videoContext.updateContext({
      ...this.props.videoContext,
      fullScreen: newValue
    })
  }

  updatePageScrolling(newValue: string) {
    if (!document) return
    const body = document.getElementsByTagName('body') && document.getElementsByTagName('body')[0]
    if (!body) return
    body.style.overflow = newValue
  }

  /* onEvent */

  onLoadError() {
    const err = this.wrappers.videoPlayer && this.wrappers.videoPlayer.error
    if (err) {
      // https://developer.mozilla.org/en-US/docs/Web/API/MediaError/code
      // Justification in PVWebXPL-398
      if (err.code === MediaError.MEDIA_ERR_DECODE) {
        fireDraperTrackingEvent(this.props.prerolls, 'error')
      } else {
        fireDraperTrackingEvent(this.props.prerolls, 'playbackBlocked')
      }
    }

    counter(`${PLAYER_COUNTER_PREFIX}::loadError`, 1, true)

    setTimeout(() => {
      this.stop()
    }, LOAD_ERROR_TIMEOUT)
  }

  onMute(muted: boolean) {
    if (global.__DEVSERVER__) return
    if (muted) {
      fireDraperTrackingEvent(this.props.prerolls, 'mute')
      fetch(this.props.prerolls.ipbTrackingEvents.mute)
    } else {
      fireDraperTrackingEvent(this.props.prerolls, 'unmute')
      fetch(this.props.prerolls.ipbTrackingEvents.unmute)
    }
  }

  onPlaybackComplete() {
    fireDraperTrackingEvent(this.props.prerolls, 'complete')
    this.props.parentListeners && this.props.parentListeners.onPlayComplete && this.props.parentListeners.onPlayComplete()
    return this.stop()
  }

  onPlayError(errorName: String) {
    // https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/play
    if (errorName === 'NotAllowedError') {
      fireDraperTrackingEvent(this.props.prerolls, 'playbackBlocked')
    } else {
      fireDraperTrackingEvent(this.props.prerolls, 'error')
    }
  }

  onPlayStart() {
    counter(`${PLAYER_COUNTER_PREFIX}::BUFFER::INITIALISED`, 1, true)
    this.videoStarted = true
    this.setState({ playing: true, paused: false }, () => {
      this.props.parentListeners && this.props.parentListeners.onPlayStart && this.props.parentListeners.onPlayStart()
      // In case the user has hovered on something else while we've been setting up
      if (!this.props.playerIsActive && this.props.videoContext.activeActivePlayer) {
        this.pauseButKeepVisible()
      }
    })
  }

  onTimeUpdate() {
    const duration = this.wrappers.videoPlayer && this.wrappers.videoPlayer.duration,
      currentTime = this.wrappers.videoPlayer && this.wrappers.videoPlayer.currentTime
    if (!duration || !currentTime) {
      return
    }


    if (!this.thirdQuartileReached && currentTime >= duration * 0.75) {
      this.thirdQuartileReached = true
      fireDraperTrackingEvent(this.props.prerolls, 'thirdQuartile')
    } else if (!this.midpointReached && currentTime >= duration * 0.5) {
      this.midpointReached = true
      fireDraperTrackingEvent(this.props.prerolls, 'midpoint')
    } else if (!this.firstQuartileReached && currentTime >= duration * 0.25) {
      this.firstQuartileReached = true
      fireDraperTrackingEvent(this.props.prerolls, 'firstQuartile')
    }
  }

  onWaiting() {
    counter(`${PLAYER_COUNTER_PREFIX}::BUFFER::REINITIALISING`, 1, true)
  }

  /* Add and remove event listeners */

  addEventListeners() {
    this.wrappers.videoPlayer.addEventListener('canplaythrough', this.playHandler)
    this.wrappers.videoPlayer.addEventListener('ended', this.stopHandler)
    this.wrappers.videoPlayer.addEventListener('timeupdate', this.updateHandler)
    this.wrappers.videoPlayer.addEventListener('waiting', this.waitingHandler)
    this.wrappers.videoPlayer.addEventListener('error', this.errorHandler)
  }

  removeEventListeners() {
    this.stopHandler &&
      this.wrappers.videoPlayer.removeEventListener('ended', this.stopHandler)
    this.playHandler &&
      this.wrappers.videoPlayer.removeEventListener('canplaythrough', this.playHandler)
    this.updateHandler &&
      this.wrappers.videoPlayer.removeEventListener('timeupdate', this.updateHandler)
    this.errorHandler &&
      this.wrappers.videoPlayer.removeEventListener('error', this.errorHandler)
    this.waitingHandler &&
      this.wrappers.videoPlayer.removeEventListener('waiting', this.waitingHandler)
  }

  /* Rendering */

  styleFromPlacementType() {
    switch (this.props.placement) {
      case DraperPlayerPlacement.Superhero:
        return styles.superhero
      case DraperPlayerPlacement.Packshot:
        return styles.packshot
      case DraperPlayerPlacement.IntermissionHero:
        return styles.intermissionHero
    }
  }
  render() {
    if (this.state.hasError || !this.props.prerolls || !this.props.prerolls.mediaFile || !this.props.videoContext) {
      return null
    }

    const hidePlayer = !this.state.playing && !this.state.paused && !this.props.videoContext.fullScreen

    return (
      <a
        className={cn(
          styles.preRollPlayer, this.styleFromPlacementType(),
          'tst-video-overlay-player-overlay', 'tst-preroll-player',
          {
            [styles.hide]: hidePlayer,
            [styles.show]: !hidePlayer,
            [styles.fullScreen]: this.props.videoContext.fullScreen && hasLock(this.props, this)
          }
        )}
        href={this.props.link} ref={
          (elem) => {
            this.wrappers.videoWrapper = elem
          }
        }>
        <video
          className={'tst-video-overlay-player-html5'}
          muted={this.props.videoContext.muted}
          src={this.props.prerolls.mediaFile} ref={
            (elem) => {
              this.wrappers.videoPlayer = elem
            }}
        />
        <DraperPlayerControls
          disableUnmute={this.props.prerolls.disableUnmute}
          toggleMute={this.toggleMute}
          enableFullscreen={this.props.enableFullscreen}
          toggleFullscreen={this.toggleFullscreen}
          videoContext={this.props.videoContext}
          placement={this.props.placement}
        />
        {this.props.placement === DraperPlayerPlacement.Superhero && <div className={styles.videoFadeOverlay} />}
      </a>

    )
  }
}
